import { TextField, Tooltip } from '@mui/material';
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isValid } from 'date-fns';
import sr_cyr from 'date-fns/locale/sr';
import sr from 'date-fns/locale/sr-Latn';
import { isUndefined } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { fetchLocaleFromLocalStorage } from '../../utils';
import withTranslations from '../../utils/HighOrderComponent';
import ErrorLabel from './ErrorLabel';

const localeMap = {
  sr,
  sr_cyr,
};

const DatePicker = (props) => {
  const {
      t,
      value,
      name,
      label,
      disabled,
      onChange,
      error,
      minDate,
      maxDate,
      required,
      minDateMessage,
      margin,
      variant,
      size,
      fullWidth,
      errorsHandler,
      readOnly,
      placement,
      disableFuture,
      disablePast,
      classNameTextField,
      tooltipText,
    } = props || {},
    { Placeholders } = t || {};

  const activeLocale = fetchLocaleFromLocalStorage();
  const inputFormat = process.env.REACT_APP_DATE_FORMAT;
  const isSlashFormat = inputFormat.includes('/');
  const placeholder = isSlashFormat
    ? Placeholders.dateFormat
    : Placeholders.dotDateFormat;

  const separator = isSlashFormat ? '/' : '.';

  const textFieldRef = useRef();

  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const insertedDate = isValid(value) ? value : null;
    setSelectedDate(insertedDate);
  }, [value]);

  const errorMessage = error?.[name];
  const hasError = !isUndefined(errorMessage);

  const onDateChangeLocalHandler = (newValue) => {
    if (newValue === null) {
      setSelectedDate(newValue);
      onChange?.(newValue, name);
    }
    if (isValid(newValue)) {
      setSelectedDate(newValue);
      onChange?.(newValue, name);
      hasError && errorRemovalHandler();
    }
  };

  const errorRemovalHandler = () => {
    errorsHandler((prevError) => {
      delete prevError[name];
      return prevError;
    });
  };

  const cursorPositionHandler = (event) => {
    const value = event?.target?.value?.length + 1;
    textFieldRef?.current?.setSelectionRange(value, value);
  };

  const parseDigits = (string) => (string.match(/\d+/g) || []).join('');

  const formatDate = (string) => {
    const digits = parseDigits(string);
    let chars = digits.split('');
    chars = chars
      .reduce((r, v, index) => {
        if (index === 0 && v > 3) return `${0}${v}`;
        if (index === 1) {
          if (+`${r}${v}` > 31) return `${3}${1}`;
          if (+r === 0 && +v === 0) return `${0}${1}`;
        }
        if (index === 2 && v > 1) return `${r}${separator}${0}${v}`;
        if (index === 3) {
          const monthPart = +r.slice(-1);
          if (monthPart === 1 && +`${monthPart}${v}` > 12)
            return `${r}${separator}${2}`;
          if (monthPart === 0 && +v === 0) return `${r}${separator}${1}`;
        }
        if (index === 7) {
          const yearPart = r.slice(-3);
          if (yearPart === '000' && +v === 0) return `${r}${1}`;
        }
        return index === 2 || index === 4 ? `${r}${separator}${v}` : `${r}${v}`;
      }, '')
      .substr(0, 10);
    return chars;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={localeMap[activeLocale]}
    >
      <MuiDatePicker
        label={label}
        inputFormat={inputFormat}
        value={selectedDate}
        onChange={onDateChangeLocalHandler}
        inputRef={textFieldRef}
        renderInput={(params) => {
          return (
            <InputField
              {...{
                ...params,
                inputProps: {
                  ...params.inputProps,
                  placeholder,
                },
              }}
              id={`date-picker-${name}`}
              InputLabelProps={{
                shrink: true,
              }}
              className={classNameTextField}
              required={!!required}
              margin={margin || 'normal'}
              error={hasError}
              size={size || 'small'}
              fullWidth={fullWidth || true}
              onKeyDown={cursorPositionHandler}
              onKeyUp={cursorPositionHandler}
              tooltiptext={tooltipText}
            />
          );
        }}
        variant={variant || 'outlined'}
        disabled={!!disabled}
        readOnly={!!readOnly}
        autoOk={true}
        minDate={minDate}
        maxDate={maxDate}
        minDateMessage={minDateMessage}
        PopperProps={{
          placement: placement || 'auto',
        }}
        reduceAnimations={true}
        disablePast={!!disablePast}
        disableFuture={!!disableFuture}
        rifmFormatter={formatDate}
      />
      <ErrorLabel error={errorMessage} />
    </LocalizationProvider>
  );
};

const InputField = (props) => {
  const { tooltiptext } = props || {};
  return tooltiptext ? (
    <ToolTipTextField {...props} />
  ) : (
    <TextField {...props} />
  );
};

const ToolTipTextField = (props) => {
  const { tooltiptext } = props || {};
  return (
    <Tooltip title={tooltiptext} placement={'top'} arrow={true}>
      <div>
        <TextField {...props} />
      </div>
    </Tooltip>
  );
};

export default withTranslations(DatePicker, 'DatePicker');
